/**
 * SidebarStep3 - Financial Data Entry (v2 Sidebar Layout)
 * 
 * This component wraps the existing Step3 form logic with the new sidebar layout.
 * It has three primary modes:
 *   1. Method Selection - Choose between Upload or Industry Templates
 *   2. Upload Mode - Before files are uploaded (drag & drop, file selection)
 *   3. Review Mode - After files are uploaded (data summary, navigation, editing)
 * 
 * EXACT same functionality as original Step3.tsx - only layout changes.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  AlertTriangle, 
  FileText,
  BarChart3,
  CreditCard,
  Folder,
  FileWarning,
  Plus,
  RefreshCw,
  Calendar,
  Check,
  Archive,
  LayoutTemplate,
  Sparkles,
  MessageSquare,
  Send,
  Mail,
  ChevronDown,
  X,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import { hasEnoughDataForDetection } from '../../../utils/financialDataNormalizer';
import api from '../../../utils/api';

// Import existing components for reuse
import UnifiedUploader from '../UnifiedUploader';
import TransactionViewer, { FlaggedTransaction } from '../../TransactionViewer';
import AnomalyBanner from '../AnomalyBanner';
import AnomalyResolver from '../AnomalyResolver';
import EventCoveragePeriod from '../EventCoveragePeriod';
import TemplateSelector from '../../TemplateSelector';
import DocumentChecklist from '../DocumentChecklist';
import TaxReconciliation from '../TaxReconciliation';

/**
 * Build financial data structure from canonical_data periods
 * Uses the pre-computed aggregates from backend (gross_revenue, employee_wages, etc.)
 * If aggregates are $0 but transactions exist, returns data for display 
 * (actual recalculation should be done via backend API)
 */
const buildFinancialDataFromCanonical = (canonicalData: any): Record<string, any[]> | null => {
  if (!canonicalData?.periods?.length) return null;
  
  const yearlyData: Record<string, any[]> = {};
  const years: number[] = [];
  
  canonicalData.periods.forEach((period: any) => {
    const year = period.year;
    const month = period.month || 1;
    
    if (!years.includes(year)) years.push(year);
    
    const yearKey = `data_${year}`;
    if (!yearlyData[yearKey]) yearlyData[yearKey] = [];
    
    // Use backend-computed aggregates
    const transactions = period.transactions || [];
    
    yearlyData[yearKey].push({
      year,
      month,
      revenue: period.gross_revenue || 0,
      cogs: period.cogs || 0,
      payroll: (period.employee_wages || 0) + (period.officer_compensation || 0),
      employee_wages: period.employee_wages || 0,
      officer_compensation: period.officer_compensation || 0,
      fixed_costs: period.fixed_costs || 0,
      variable_costs: period.variable_costs || 0,
      net_profit: period.net_profit || 0,
      line_items: period.line_items || {},
      transaction_count: transactions.length,
      transactions,
    });
  });
  
  // Sort data
  years.sort((a, b) => a - b);
  Object.keys(yearlyData).forEach(yearKey => {
    yearlyData[yearKey].sort((a: any, b: any) => a.month - b.month);
  });
  
  return yearlyData;
};

/**
 * Build canonical_data structure from financial_data (reverse of above)
 * Used when anomaly detection needs canonical format but we only have financial_data
 */
const buildCanonicalFromFinancialData = (financialData: Record<string, any[]>): { periods: any[] } | null => {
  if (!financialData) return null;
  
  const periods: any[] = [];
  
  Object.entries(financialData)
    .filter(([key]) => key.startsWith('data_'))
    .forEach(([yearKey, months]) => {
      if (!Array.isArray(months)) return;
      
      months.forEach((monthData: any) => {
        periods.push({
          year: monthData.year || parseInt(yearKey.replace('data_', ''), 10),
          month: monthData.month,
          gross_revenue: monthData.revenue || 0,
          cogs: monthData.cogs || 0,
          employee_wages: monthData.employee_wages || monthData.payroll || 0,
          officer_compensation: monthData.officer_compensation || 0,
          fixed_costs: monthData.fixed_costs || 0,
          variable_costs: monthData.variable_costs || 0,
          net_profit: monthData.net_profit || 0,
          transactions: monthData.transactions || [],
        });
      });
    });
  
  // Sort periods by year then month
  periods.sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
  
  return periods.length > 0 ? { periods } : null;
};

interface SidebarStep3Props {
  goToStep?: (step: number) => void;
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  initialSection?: string | null; // For deep linking from URL params
  highestCompletedStep?: number;
  claimId?: string;
}

type ViewMode = 'upload' | 'files' | 'summary' | 'monthly' | 'transactions' | 'categories' | 'tax' | 'anomalies' | 'template' | 'template_complete';

type EntryMethod = 'select' | 'upload' | 'template';

export default function SidebarStep3({ 
  onNext, 
  onBack, 
  data, 
  onDataUpdate, 
  onSave,
  isSaving,
  lastSaved,
  goToStep,
  initialSection,
  highestCompletedStep,
  claimId
}: SidebarStep3Props) {
  // Determine initial mode based on existing data
  // Check if we have existing data from any source
  const hasExistingData = useMemo(() => {
    const hasFromFlags = data.fromSpreadsheet || data.fromCleanup || data.fromQuickBooks || 
                         data.fromTemplate || data.fromUnifiedUpload;
    const hasFinancialDataKeys = data.financial_data && 
                                 Object.keys(data.financial_data).some(k => k.startsWith('data_'));
    const result = hasFromFlags || hasFinancialDataKeys;
    console.log('🔍 hasExistingData:', result, { hasFromFlags, hasFinancialDataKeys, financial_data_keys: Object.keys(data.financial_data || {}) });
    return result;
  }, [data.fromSpreadsheet, data.fromCleanup, data.fromQuickBooks, data.fromTemplate, data.fromUnifiedUpload, data.financial_data]);

  // Entry method selection state
  const [entryMethod, setEntryMethod] = useState<EntryMethod>(
    hasExistingData ? 'upload' : 'select'
  );
  
  // Compute shouldShowMethodSelection early - used in multiple places including auto-advance logic
  const shouldShowMethodSelection = entryMethod === 'select' && !hasExistingData;
  
  // Template data state
  const [appliedTemplate, setAppliedTemplate] = useState<any>(data.applied_template || null);

  // Determine initial view - prioritize deep link section if provided and data exists
  const getInitialView = (): ViewMode => {
    if (initialSection && hasExistingData) {
      const validSections: ViewMode[] = ['summary', 'monthly', 'transactions', 'categories', 'tax', 'anomalies'];
      if (validSections.includes(initialSection as ViewMode)) {
        console.log('🔗 Deep link: Setting initial view to', initialSection);
        return initialSection as ViewMode;
      }
    }
    if (data.fromTemplate) return 'template_complete';
    if (hasExistingData) return 'summary';
    return 'upload';
  };

  // State management (replicated from original Step3.tsx)
  const [isUploadMode, setIsUploadMode] = useState(!hasExistingData);
  const [activeView, setActiveView] = useState<ViewMode>(getInitialView());
  
  // Financial data state
  const [aiExtractedData, setAiExtractedData] = useState<any>(() => {
    // Priority 1: Check unified_upload_data.canonical_data (has transactions + computed aggregates)
    if (data.unified_upload_data?.canonical_data?.periods?.length > 0) {
      const computed = buildFinancialDataFromCanonical(data.unified_upload_data.canonical_data);
      if (computed) {
        console.log('📊 Built financial data from canonical_data');
        return computed;
      }
    }
    
    // Priority 2: Use existing financial_data if available
    if (data.financial_data && Object.keys(data.financial_data).length > 0) {
      return data.financial_data;
    }
    
    return null;
  });
  const [unifiedUploadData, setUnifiedUploadData] = useState<any>(data.unified_upload_data || null);
  const [tax1065Files, setTax1065Files] = useState<any[]>(data.tax1065Files || []);
  const [plFiles, setPlFiles] = useState<any[]>(data.plFiles || []);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>(data.uploadedDocuments || []);
  
  // Event coverage state
  const [eventStartDate, setEventStartDate] = useState(data.event_start_date || '');
  const [eventEndDate, setEventEndDate] = useState(data.event_end_date || '');
  const [femaDeclarationNumber, setFemaDeclarationNumber] = useState(data.fema_declaration_number || '');
  
  // Anomaly state - MUST be declared before useEffect that uses it
  const [highlightedTransactionId, setHighlightedTransactionId] = useState<string | null>(null);
  const [resolvedAnomalies, setResolvedAnomalies] = useState<Array<{id: string; note: string; resolved_at: string}>>(
    data.resolved_anomalies || []
  );
  const [cachedAnomalyAnalysis, setCachedAnomalyAnalysis] = useState<{
    anomalies: any[];
    dataHash: string;
    analyzedAt: string;
  } | null>(data.cached_anomaly_analysis || null);
  
  // Auto-detect event dates from anomaly analysis when analysis completes
  useEffect(() => {
    // Skip if dates already set
    if (eventStartDate && eventEndDate) return;
    
    // Need cached anomaly analysis to detect dates
    const anomalies = cachedAnomalyAnalysis?.anomalies || [];
    if (anomalies.length === 0) return;
    
    // Find significant revenue drops (>40%, not January seasonal)
    const revenueDrops = anomalies.filter((a: any) => 
      a.anomaly_type === 'revenue_drop' && 
      a.affected_period &&
      !a.affected_period.endsWith('-01') &&
      Math.abs(a.deviation_percent || 0) > 40
    );
    
    if (revenueDrops.length > 0) {
      const biggestDrop = revenueDrops.reduce((max: any, curr: any) => 
        Math.abs(curr.deviation_percent || 0) > Math.abs(max.deviation_percent || 0) ? curr : max
      );
      
      const [year, month] = biggestDrop.affected_period.split('-').map(Number);
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endDate = `${year + 1}-${String(month).padStart(2, '0')}-01`;
      
      if (!eventStartDate) {
        setEventStartDate(startDate);
        if (onDataUpdate) onDataUpdate({ event_start_date: startDate });
        console.log(`📅 Auto-detected loss event start: ${startDate}`);
      }
      if (!eventEndDate) {
        setEventEndDate(endDate);
        if (onDataUpdate) onDataUpdate({ event_end_date: endDate });
        console.log(`📅 Auto-detected loss event end: ${endDate}`);
      }
    }
  }, [cachedAnomalyAnalysis?.anomalies, eventStartDate, eventEndDate, onDataUpdate]);

  // Client clarification state
  const [flaggedTransactions, setFlaggedTransactions] = useState<FlaggedTransaction[]>(
    data.flagged_transactions || []
  );
  const [showClarificationPanel, setShowClarificationPanel] = useState(false);
  const [sendingClarification, setSendingClarification] = useState(false);
  const [clarificationSent, setClarificationSent] = useState(false);
  const [clientEmail, setClientEmail] = useState(data.client_email || data.claimant_email || '');

  // Event date validation state
  const [eventDateValidation, setEventDateValidation] = useState<{
    is_valid: boolean;
    confidence: string;
    warnings: string[];
    suggestions: string[];
    auto_detected_date: string | null;
  } | null>(null);
  
  // Files view state - for showing uploaded S3 files
  const [s3Files, setS3Files] = useState<any[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [reprocessingFileId, setReprocessingFileId] = useState<string | null>(null);
  
  // Client portal sync state
  const [isSyncingClientUploads, setIsSyncingClientUploads] = useState(false);
  const [syncAttempted, setSyncAttempted] = useState(false);
  
  // Inline validation/status messages (replacing alert() calls)
  const [validationError, setValidationError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Recalculate aggregates state
  const [isRecalculating, setIsRecalculating] = useState(false);
  
  // Diagnostics state
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState<any>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // Run diagnostics on financial data
  const handleDiagnose = async () => {
    const draftId = claimId || data?.id || data?.draft_id;
    if (!draftId) {
      console.error('No draft ID available for diagnostics');
      return;
    }
    
    setIsDiagnosing(true);
    try {
      console.log('🔍 Running diagnostics for draft:', draftId);
      const response = await api.get(`/api/v1/upload-savefirst/diagnostics/${draftId}`);
      
      setDiagnosticResults(response);
      setShowDiagnostics(true);
      console.log('✅ Diagnostics complete:', response);
    } catch (error) {
      console.error('Error running diagnostics:', error);
      setValidationError('Failed to run diagnostics. Please try again.');
    } finally {
      setIsDiagnosing(false);
    }
  };

  // Fix identified issues
  const handleFixIssues = async () => {
    const draftId = claimId || data?.id || data?.draft_id;
    if (!draftId) return;
    
    setIsRecalculating(true);
    try {
      console.log('🔧 Fixing issues for draft:', draftId);
      const response = await api.post(`/api/v1/upload-savefirst/fix-issues/${draftId}`, {
        fix_types: ['aggregation', 'cogs']
      });
      
      if (response.success) {
        console.log('✅ Fixes applied:', response);
        setShowDiagnostics(false);
        setDiagnosticResults(null);
        // Reload to show updated data
        window.location.reload();
      } else {
        setValidationError(`Fix failed: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fixing issues:', error);
      setValidationError('Failed to fix issues. Please try again.');
    } finally {
      setIsRecalculating(false);
    }
  };

  // Recalculate aggregates from transactions using backend logic
  const handleRecalculateAggregates = async () => {
    const draftId = claimId || data?.id || data?.draft_id;
    if (!draftId) {
      console.error('No draft ID available for recalculation');
      return;
    }
    
    setIsRecalculating(true);
    try {
      console.log('📊 Recalculating aggregates for draft:', draftId);
      const response = await api.post('/api/v1/upload-savefirst/recalculate-aggregates', {
        draft_id: draftId
      });
      
      if (response.success) {
        console.log('✅ Recalculation complete:', response);
        
        // Reload the page to get fresh data
        // Or update the state directly if we have the updated canonical_data
        window.location.reload();
      } else {
        console.error('Recalculation failed:', response.message);
        setValidationError(`Recalculation failed: ${response.message}`);
      }
    } catch (error) {
      console.error('Error recalculating aggregates:', error);
      setValidationError('Failed to recalculate aggregates. Please try again.');
    } finally {
      setIsRecalculating(false);
    }
  };

  // Build files list from draft data (plFiles, tax1065Files, bankStatementFiles)
  // This is the primary source - these are the actual files associated with this case
  const getFilesFromDraftData = (): any[] => {
    const files: any[] = [];
    
    // P&L files
    const plFiles = data?.plFiles || [];
    plFiles.forEach((f: any, idx: number) => {
      files.push({
        upload_id: `pl-${idx}`,
        filename: f.filename || f.name || `P&L Document ${idx + 1}`,
        original_filename: f.filename || f.name || `P&L Document ${idx + 1}`,
        file_type: 'pdf',
        status: 'parsed',
        extraction_method: f.extraction_method || 'ai_extraction',
        transactions_count: f.transactions?.length || f.data?.transactions?.length || 0,
        source: 'draft_data',
        doc_type: 'pl',
      });
    });
    
    // Tax 1065 files
    const tax1065Files = data?.tax1065Files || [];
    tax1065Files.forEach((f: any, idx: number) => {
      files.push({
        upload_id: `tax-${idx}`,
        filename: f.filename || f.name || `Form 1065 (${f.year || 'Unknown Year'})`,
        original_filename: f.filename || f.name || `Form 1065 (${f.year || 'Unknown Year'})`,
        file_type: 'pdf',
        status: 'parsed',
        extraction_method: f.extraction_method || 'tax_parser',
        transactions_count: 0, // Tax forms don't have transaction counts
        source: 'draft_data',
        doc_type: 'tax1065',
        year: f.year,
        form_type: f.form_type,
      });
    });
    
    // Bank statement files
    const bankFiles = data?.bankStatementFiles || [];
    bankFiles.forEach((f: any, idx: number) => {
      files.push({
        upload_id: `bank-${idx}`,
        filename: f.filename || f.name || `Bank Statement ${idx + 1}`,
        original_filename: f.filename || f.name || `Bank Statement ${idx + 1}`,
        file_type: f.file_type || 'pdf',
        status: 'parsed',
        extraction_method: f.extraction_method || 'bank_parser',
        transactions_count: f.transactions?.length || 0,
        source: 'draft_data',
        doc_type: 'bank_statement',
      });
    });
    
    return files;
  };

  // Fetch uploaded files - use draft data first, then fall back to uploaded_files collection
  const fetchUploadedFiles = async () => {
    setFilesLoading(true);
    
    try {
      // Get case ID for querying
      const caseId = claimId || data?.id || data?.draft_id;
      
      // PRIORITY 1: Check uploaded_files collection (S3-backed files with proper linking)
      if (caseId) {
        // Query by both claim_id and draft_id since either could be set
        const response = await api.get(`/api/v1/upload-savefirst/reprocess/available?claim_id=${caseId}&draft_id=${caseId}&limit=500`);
        if (response?.files?.length > 0) {
          setS3Files(response.files);
          console.log(`📁 Loaded ${response.files.length} files from uploaded_files collection`);
          return;
        }
      }
      
      // PRIORITY 2: Get files from draft data (plFiles, tax1065Files, bankStatementFiles)
      // These are legacy files stored inline in the draft, not in S3
      const draftFiles = getFilesFromDraftData();
      if (draftFiles.length > 0) {
        setS3Files(draftFiles);
        console.log(`📁 Loaded ${draftFiles.length} files from draft data (legacy)`);
        return;
      }
      
      // No files found in either source
      setS3Files([]);
      console.log('📁 No files found for this case');
    } catch (err) {
      console.error('Failed to fetch uploaded files:', err);
      setS3Files([]);
    } finally {
      setFilesLoading(false);
    }
  };


  // Reprocess a single failed file
  const handleReprocessFile = async (uploadId: string) => {
    setReprocessingFileId(uploadId);
    try {
      const response = await api.post('/api/v1/upload-savefirst/reprocess', {
        upload_ids: [uploadId],
        limit: 1
      });
      
      if (response.results?.[0]?.status === 'success') {
        // Refresh the files list
        await fetchUploadedFiles();
      }
    } catch (err) {
      console.error('Reprocess failed:', err);
    } finally {
      setReprocessingFileId(null);
    }
  };

  // Load files when switching to files view OR on mount
  useEffect(() => {
    if (activeView === 'files' || s3Files.length === 0) {
      fetchUploadedFiles();
    }
  }, [activeView, claimId]);
  
  // Also fetch files on initial mount
  useEffect(() => {
    fetchUploadedFiles();
  }, []);

  // Sync tax1065Files when data prop changes (e.g., after page refresh or upload)
  useEffect(() => {
    if (data.tax1065Files && data.tax1065Files.length > 0) {
      setTax1065Files(data.tax1065Files);
      console.log('📋 Synced tax1065Files from data:', data.tax1065Files.length, 'items');
    }
  }, [data.tax1065Files]);

  // Sync client portal uploads to draft if needed
  // This runs when we have uploaded_files but no financial_data
  const syncClientUploads = async () => {
    const draftId = claimId || data?.id || data?.draft_id;
    if (!draftId || isSyncingClientUploads || syncAttempted) return;
    
    console.log('🔄 Checking if client portal sync is needed...');
    
    // ALWAYS sync if data came from client portal - categories may need updating
    const isFromClientPortal = data.fromClientPortal === true;
    
    // Check if we already have valid financial data (not from client portal)
    const hasFinancialData = data.financial_data && 
      Object.keys(data.financial_data).some(k => k.startsWith('data_'));
    
    // Skip only if we have financial data AND it's NOT from client portal
    if (hasFinancialData && !isFromClientPortal) {
      console.log('📊 Already have financial data (not from client portal), skipping sync');
      return;
    }
    
    // Check if there are uploaded_files for this draft
    try {
      const filesResponse = await api.get(`/api/v1/upload-savefirst/reprocess/available?claim_id=${draftId}&draft_id=${draftId}&limit=10`);
      
      if (!filesResponse?.files || filesResponse.files.length === 0) {
        console.log('📁 No uploaded files found, skipping sync');
        return;
      }
      
      // Check if any files are parsed
      const parsedFiles = filesResponse.files.filter((f: any) => f.status === 'parsed');
      if (parsedFiles.length === 0) {
        console.log('📁 No parsed files yet, skipping sync');
        return;
      }
      
      console.log(`🔄 Found ${parsedFiles.length} parsed files, syncing to draft...`);
      setIsSyncingClientUploads(true);
      setSyncAttempted(true);
      
      // Call sync endpoint
      const syncResult = await api.post(`/api/v1/reports/drafts/${draftId}/sync-client-uploads`);
      
      if (syncResult.success) {
        console.log(`✅ Synced ${syncResult.transactions_synced} transactions from ${syncResult.files_synced} files`);
        
        // Update local state with synced data
        if (syncResult.canonical_data) {
          const financialData = buildFinancialDataFromCanonical(syncResult.canonical_data);
          if (financialData) {
            setAiExtractedData(financialData);
            setUnifiedUploadData({
              canonical_data: syncResult.canonical_data,
              file_results: [],
              stats: {
                total_files: syncResult.files_synced,
                total_transactions: syncResult.transactions_synced,
              },
              review_items: [],
            });
            setIsUploadMode(false);
            setActiveView('summary');
            setEntryMethod('upload');
            
            // Update wizard data
            if (onDataUpdate) {
              onDataUpdate({
                ...data,
                financial_data: financialData,
                unified_upload_data: syncResult.canonical_data,
                fromUnifiedUpload: true,
                fromClientPortal: true,
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to sync client uploads:', err);
    } finally {
      setIsSyncingClientUploads(false);
    }
  };
  
  // Run sync check on mount
  useEffect(() => {
    syncClientUploads();
  }, [claimId, data?.id, data?.draft_id]);

  // Validate event date when financial data or event date changes
  useEffect(() => {
    const validateEventDate = async () => {
      // Only validate if we have both event date and financial data
      if (!eventStartDate || !data.financial_data) {
        setEventDateValidation(null);
        return;
      }
      
      // Check if we have year-keyed data
      const hasYearData = Object.keys(data.financial_data).some(k => k.startsWith('data_'));
      if (!hasYearData) {
        setEventDateValidation(null);
        return;
      }
      
      try {
        // Use centralized API utility
        const result = await api.post('/api/v1/reports/validate-event-date', {
          event_start_date: eventStartDate,
          financial_data: data.financial_data
        }, { showErrorToast: false });
        
        if (result.success) {
          setEventDateValidation(result.validation);
          console.log('📅 Event date validation:', result.validation);
        }
      } catch (error) {
        console.error('Event date validation error:', error);
      }
    };
    
    // Debounce validation
    const timeoutId = setTimeout(validateEventDate, 500);
    return () => clearTimeout(timeoutId);
  }, [eventStartDate, data.financial_data]);

  // Compute statistics from uploaded data OR from saved financial_data
  const stats = unifiedUploadData?.stats || {};
  const periods = unifiedUploadData?.canonical_data?.periods || [];
  
  // Also check aiExtractedData/financial_data for transaction counts
  const financialDataForStats = aiExtractedData || data.financial_data || {};
  
  // Debug logging
  console.log('📊 Stats computation:', {
    hasUnifiedUploadData: !!unifiedUploadData,
    hasAiExtractedData: !!aiExtractedData,
    aiExtractedDataKeys: Object.keys(aiExtractedData || {}),
    hasDataFinancialData: !!data.financial_data,
    dataFinancialDataKeys: Object.keys(data.financial_data || {}),
    financialDataForStatsKeys: Object.keys(financialDataForStats),
    isUploadMode,
  });
  
  const financialDataTransactionCount = Object.values(financialDataForStats)
    .filter((v): v is any[] => Array.isArray(v))
    .reduce((sum, yearData) => {
      return sum + yearData.reduce((ySum: number, month: any) => ySum + (month.transaction_count || 0), 0);
    }, 0);
  
  const financialDataPeriodCount = Object.values(financialDataForStats)
    .filter((v): v is any[] => Array.isArray(v))
    .reduce((sum, yearData) => sum + yearData.length, 0);
  
  // Calculate derived stats - prefer canonical data, fallback to financial_data
  const totalTransactions = stats.total_transactions || 
    periods.reduce((sum: number, p: any) => sum + (p.transactions?.length || 0), 0) ||
    financialDataTransactionCount;
  const totalPeriods = stats.total_periods || periods.length || financialDataPeriodCount;
  const totalFiles = stats.files_processed || 0;
  const categoriesCount = new Set(periods.flatMap((p: any) => (p.transactions || []).map((t: any) => t.category))).size;
  
  // Calculate items needing review
  const itemsNeedingReview = (() => {
    if (!periods.length) return stats.items_needing_review || 0;
    let needsReview = 0;
    // Expanded clear-cut categories that don't need manual review at high confidence
    const CLEAR_CUT = new Set([
      // Standard accounting categories
      'Utilities', 'utilities', 'UTILITIES',
      'Rent & Occupancy', 'Rent', 'rent', 'RENT',
      'Payroll & Benefits', 'Payroll', 'payroll', 'PAYROLL',
      'Insurance', 'insurance', 'INSURANCE',
      'Bank & Financial Fees', 'Bank Fees', 'BANK_FEES', 'bank_fees',
      'Sales Revenue', 'Revenue', 'revenue', 'REVENUE',
      'COGS', 'cogs', 'Cost of Goods Sold',
      'Inventory & Merchandise', 'inventory',
      'Food & Beverage Cost', 'food', 'FOOD',
      // Technology & software - common business expense
      'Technology & Software', 'technology & software', 'TECHNOLOGY',
      // Supplies & equipment
      'Supplies', 'supplies', 'SUPPLIES',
      'Equipment', 'equipment', 'EQUIPMENT',
      // Other common categories
      'Delivery', 'DELIVERY', 'delivery',
      'Marketing', 'MARKETING', 'marketing',
      'Professional Services', 'professional',
      'Taxes & Licenses', 'taxes',
      'Repairs & Maintenance', 'repairs',
    ]);
    for (const period of periods) {
      for (const txn of (period.transactions || [])) {
        const conf = txn.classification?.confidence || txn.confidence || 0;
        const cat = txn.category || '';
        const catLower = cat.toLowerCase();
        // Transaction needs review if:
        // 1. Category is uncategorized/unknown
        // 2. Confidence is low (<0.85) for any category
        // 3. Confidence is between 0.85-0.95 for non-clear-cut categories
        const isUncategorized = catLower === 'uncategorized' || catLower === '' || catLower === 'unknown';
        const isAutoApproved = !isUncategorized && (conf >= 0.95 || (CLEAR_CUT.has(cat) && conf >= 0.85));
        if (!isAutoApproved) needsReview++;
      }
    }
    return needsReview;
  })();

  // Get anomaly count
  const anomalyCount = cachedAnomalyAnalysis?.anomalies?.length || 0;

  // Date range calculation
  const dateRange = (() => {
    if (!periods.length) return null;
    const sorted = [...periods].sort((a, b) => {
      const dateA = new Date(a.year, (a.month || 1) - 1);
      const dateB = new Date(b.year, (b.month || 1) - 1);
      return dateA.getTime() - dateB.getTime();
    });
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[(first.month || 1) - 1]} ${first.year} - ${months[(last.month || 1) - 1]} ${last.year}`;
  })();

  // Handler for event date changes - ALSO persist to database
  const handleEventDateChange = (field: 'event_start_date' | 'event_end_date' | 'fema_declaration_number', value: string) => {
    if (field === 'event_start_date') {
      setEventStartDate(value);
      if (onDataUpdate) onDataUpdate({ event_start_date: value });
    } else if (field === 'event_end_date') {
      setEventEndDate(value);
      if (onDataUpdate) onDataUpdate({ event_end_date: value });
    } else if (field === 'fema_declaration_number') {
      setFemaDeclarationNumber(value);
      if (onDataUpdate) onDataUpdate({ fema_declaration_number: value });
    }
  };

  // Handler for anomaly resolution
  const handleResolveAnomaly = (anomalyId: string, note: string) => {
    const newResolved = [...resolvedAnomalies, { 
      id: anomalyId, 
      note, 
      resolved_at: new Date().toISOString() 
    }];
    setResolvedAnomalies(newResolved);
    if (onDataUpdate) {
      onDataUpdate({ resolved_anomalies: newResolved });
    }
  };

  const handleUnresolveAnomaly = (anomalyId: string) => {
    const newResolved = resolvedAnomalies.filter(r => r.id !== anomalyId);
    setResolvedAnomalies(newResolved);
    if (onDataUpdate) {
      onDataUpdate({ resolved_anomalies: newResolved });
    }
  };

  const handleAnalysisComplete = (analysis: { anomalies: any[]; dataHash: string; analyzedAt: string }) => {
    setCachedAnomalyAnalysis(analysis);
    if (onDataUpdate) {
      onDataUpdate({ cached_anomaly_analysis: analysis });
    }
  };

  // Handle forensic insight fetch - cache to database
  const handleForensicInsightFetched = (anomalyId: string, insight: any) => {
    // Update local state
    setCachedAnomalyAnalysis((prev: any) => {
      if (!prev || !prev.anomalies) return prev;
      
      const updatedAnomalies = prev.anomalies.map((a: any) => {
        if (a.id === anomalyId) {
          return {
            ...a,
            forensic_insight: insight.forensic_insight,
            forensic_analysis: insight,
            forensic_analyzed_at: new Date().toISOString()
          };
        }
        return a;
      });
      
      const updated = { ...prev, anomalies: updatedAnomalies };
      
      // Persist to database
      if (onDataUpdate) {
        onDataUpdate({ cached_anomaly_analysis: updated });
      }
      
      return updated;
    });
  };

  // Handle upload complete from UnifiedUploader
  const handleUploadComplete = async (result: any) => {
    console.log('📦 UnifiedUploader complete:', result);
    console.log('📊 Stats:', result.stats);
    console.log('📄 File results:', result.file_results?.length || 0, 'files');
    console.log('📋 tax1065Files from result:', result.tax1065Files?.length || 0, 'files');
    
    // Track that upload has happened
    setUnifiedUploadData(result);
    
    // ✅ CRITICAL FIX: MERGE tax1065Files from the job result with existing files
    // Don't replace - merge new files with existing ones to support batch uploads
    let mergedTaxFiles = tax1065Files; // Start with existing
    if (result.tax1065Files && result.tax1065Files.length > 0) {
      console.log('📝 Merging tax1065Files from job result');
      console.log('📝 New files:', result.tax1065Files.length, 'Existing files:', tax1065Files.length);
      
      // Merge: keep existing files, add/update from result
      const existingMap = new Map(tax1065Files.map((f: any) => [f.filename, f]));
      result.tax1065Files.forEach((newFile: any) => {
        existingMap.set(newFile.filename, newFile);
      });
      mergedTaxFiles = Array.from(existingMap.values());
      console.log('📝 Merged total:', mergedTaxFiles.length);
      setTax1065Files(mergedTaxFiles);
    }
    
    if (result.canonical_data && result.canonical_data.periods?.length > 0) {
      const yearlyData: Record<string, any[]> = {};
      const years: number[] = [];
      
      // Convert periods to data_YYYY format
      result.canonical_data.periods?.forEach((period: any) => {
        const year = period.year;
        const month = period.month || 1;
        
        if (!years.includes(year)) years.push(year);
        
        const yearKey = `data_${year}`;
        if (!yearlyData[yearKey]) yearlyData[yearKey] = [];
        
        yearlyData[yearKey].push({
          year,
          month,
          revenue: period.gross_revenue || 0,
          cogs: period.cogs || 0,
          payroll: (period.employee_wages || 0) + (period.officer_compensation || 0),
          employee_wages: period.employee_wages || 0,
          officer_compensation: period.officer_compensation || 0,
          fixed_costs: period.fixed_costs || 0,
          variable_costs: period.variable_costs || 0,
          net_profit: period.net_profit || 0,
          line_items: period.line_items || {},
          transaction_count: period.transactions?.length || 0,
        });
      });
      
      years.sort((a, b) => a - b);
      
      // Sort monthly data within each year
      Object.keys(yearlyData).forEach(yearKey => {
        yearlyData[yearKey].sort((a: any, b: any) => a.month - b.month);
      });
      
      const financialData: Record<string, any> = { ...yearlyData };
      
      // Update state
      setUnifiedUploadData(result);
      setAiExtractedData(financialData);
      if (result.tax1065Files?.length > 0) {
        setTax1065Files(result.tax1065Files);
      }
      
      // Build payroll data for Step 5 compatibility
      const payrollData: Record<string, any> = {};
      years.forEach(year => {
        const yearData = yearlyData[`data_${year}`] || [];
        const totalPayroll = yearData.reduce((sum: number, m: any) => sum + (m.payroll || 0), 0);
        const totalEmployeeWages = yearData.reduce((sum: number, m: any) => sum + (m.employee_wages || 0), 0);
        const totalOfficerComp = yearData.reduce((sum: number, m: any) => sum + (m.officer_compensation || 0), 0);
        
        payrollData[year] = {
          total: totalPayroll,
          employee_wages: totalEmployeeWages,
          officer_compensation: totalOfficerComp,
        };
      });
      
      // Update wizard data
      if (onDataUpdate) {
        const updatedData: Record<string, any> = {
          ...data,
          financial_data: financialData,
          fromUnifiedUpload: true,
          unified_upload_data: result,
          calculated_years: years,
          payroll_data: payrollData,
          tax1065Files: mergedTaxFiles,  // Use merged files, not just result
        };
        
        // Add year-specific payroll fields
        years.forEach(year => {
          updatedData[`payroll_${year}`] = payrollData[year]?.total || 0;
          updatedData[`employee_payroll_${year}`] = payrollData[year]?.employee_wages || 0;
          updatedData[`officer_compensation_${year}`] = payrollData[year]?.officer_compensation || 0;
        });
        
        const fullDataToSave = { ...data, ...updatedData };
        onDataUpdate(updatedData);
        
        // Trigger save
        if (onSave) {
          setTimeout(async () => {
            try {
              await onSave(fullDataToSave);
              console.log('✅ Save completed');
            } catch (err) {
              console.error('❌ Save failed:', err);
            }
          }, 100);
        }
      }
    } else {
      // Even without canonical_data, store file_results for display
      console.log('⚠️ No canonical_data in result, but upload complete');
      if (result.file_results?.length > 0) {
        console.log('📄 File results available:', result.file_results.length);
      }
      
      // ✅ CRITICAL: Still update wizard data with tax files even without canonical_data
      // Tax documents don't produce canonical_data, but we still need to save their extracted data
      if (onDataUpdate && mergedTaxFiles.length > 0) {
        console.log('📝 Saving tax1065Files without canonical_data, count:', mergedTaxFiles.length);
        const updatedData: Record<string, any> = {
          ...data,
          fromUnifiedUpload: true,
          unified_upload_data: result,
          tax1065Files: mergedTaxFiles,  // Use merged files
        };
        
        const fullDataToSave = { ...data, ...updatedData };
        onDataUpdate(updatedData);
        
        // Trigger save with the full data including tax files with extracted data
        if (onSave) {
          setTimeout(async () => {
            try {
              await onSave(fullDataToSave);
              console.log('✅ Save completed (tax files only)');
            } catch (err) {
              console.error('❌ Save failed:', err);
            }
          }, 100);
        }
      }
    }
    
    // Always switch to review mode after upload completes
    setIsUploadMode(false);
    setActiveView('summary');
  };

  // Handle navigation to next step
  const handleNext = () => {
    const years = Object.keys(aiExtractedData || {})
      .filter(key => key.startsWith('data_'))
      .map(key => parseInt(key.replace('data_', '')))
      .filter(y => !isNaN(y))
      .sort((a, b) => a - b);
    
    onNext({
      ...data,
      financial_data: aiExtractedData,
      fromUnifiedUpload: true,
      unified_upload_data: unifiedUploadData,
      calculated_years: years,
      tax1065Files: tax1065Files,
      event_start_date: eventStartDate,
      event_end_date: eventEndDate,
      fema_declaration_number: femaDeclarationNumber,
    });
  };

  // Auto-advance to next step when all data is ready (100% AI automation)
  // NO HUMAN CLICKS REQUIRED - if data is ready, advance automatically
  // BUT: Don't auto-advance if user is navigating BACK from a later step
  const isNavigatingBack = data.auto_advanced === true || data.step > 3;
  const [hasAutoAdvanced, setHasAutoAdvanced] = useState(isNavigatingBack);
  useEffect(() => {
    // Skip if already auto-advanced or navigating back
    if (hasAutoAdvanced) return;
    
    // Skip if in upload mode or method selection (user is still choosing)
    if (isUploadMode && !aiExtractedData) return;
    if (shouldShowMethodSelection) return;
    
    // Check if we have all required data for auto-advance
    const hasData = aiExtractedData && Object.keys(aiExtractedData).length > 0;
    const hasAnalyzedAnomalies = cachedAnomalyAnalysis?.analyzedAt;
    
    // For event dates: use what we have or auto-detect from anomalies
    let effectiveStartDate = eventStartDate;
    let effectiveEndDate = eventEndDate;
    
    if (!effectiveStartDate || !effectiveEndDate) {
      // Try to detect from anomalies
      const anomalies = cachedAnomalyAnalysis?.anomalies || [];
      const revenueDrops = anomalies.filter((a: any) => 
        a.anomaly_type === 'revenue_drop' && 
        a.affected_period &&
        !a.affected_period.endsWith('-01') &&
        Math.abs(a.deviation_percent || 0) > 40
      );
      
      if (revenueDrops.length > 0) {
        const biggestDrop = revenueDrops.reduce((max: any, curr: any) => 
          Math.abs(curr.deviation_percent || 0) > Math.abs(max.deviation_percent || 0) ? curr : max
        );
        const [year, month] = biggestDrop.affected_period.split('-').map(Number);
        effectiveStartDate = `${year}-${String(month).padStart(2, '0')}-01`;
        effectiveEndDate = `${year + 1}-${String(month).padStart(2, '0')}-01`;
      }
    }
    
    const hasEventDates = effectiveStartDate && effectiveEndDate;
    
    // AUTO-ADVANCE CONDITIONS:
    // 1. Has financial data (from uploads)
    // 2. Anomalies have been analyzed by AI
    // 3. Event dates are available (set or auto-detected)
    if (hasData && hasAnalyzedAnomalies && hasEventDates) {
      console.log('🚀 AUTO-ADVANCING: All data ready, proceeding to next step');
      console.log(`   - Financial data: ✓`);
      console.log(`   - Anomalies analyzed: ✓`);
      console.log(`   - Event dates: ${effectiveStartDate} to ${effectiveEndDate}`);
      
      setHasAutoAdvanced(true);
      
      // Update event dates if they were auto-detected
      if (!eventStartDate && effectiveStartDate) {
        setEventStartDate(effectiveStartDate);
        if (onDataUpdate) onDataUpdate({ event_start_date: effectiveStartDate });
      }
      if (!eventEndDate && effectiveEndDate) {
        setEventEndDate(effectiveEndDate);
        if (onDataUpdate) onDataUpdate({ event_end_date: effectiveEndDate });
      }
      
      // Build the data payload and advance
      const years = Object.keys(aiExtractedData || {})
        .filter(key => key.startsWith('data_'))
        .map(key => parseInt(key.replace('data_', '')))
        .filter(y => !isNaN(y))
        .sort((a, b) => a - b);
      
      // Advance immediately - no delay needed for automation
      onNext({
        ...data,
        financial_data: aiExtractedData,
        fromUnifiedUpload: true,
        unified_upload_data: unifiedUploadData,
        calculated_years: years,
        tax1065Files: tax1065Files,
        event_start_date: effectiveStartDate,
        event_end_date: effectiveEndDate,
        fema_declaration_number: femaDeclarationNumber,
        auto_advanced: true, // Flag to indicate this was automated
      });
    }
  }, [aiExtractedData, cachedAnomalyAnalysis, eventStartDate, eventEndDate, hasAutoAdvanced, isUploadMode, shouldShowMethodSelection, data, unifiedUploadData, tax1065Files, femaDeclarationNumber, onNext, onDataUpdate]);

  // Calculate completion percentage
  const hasFinancialData = aiExtractedData && Object.keys(aiExtractedData).length > 0;
  const hasTaxFiles = tax1065Files.length > 0;
  const completionPercent = Math.round(
    28 + // Previous steps
    (hasFinancialData ? 15 : 0) +
    (hasTaxFiles ? 5 : 0) +
    (eventStartDate && eventEndDate ? 5 : 0)
  );

  // Build sidebar sections based on mode
  const buildSections = () => {
    // Count uploaded files from S3 state
    const uploadedFilesCount = s3Files.length || uploadedDocuments?.length || data?.uploadedDocuments?.length || 0;
    
    if (isUploadMode) {
      return [
        { 
          id: 'upload', 
          label: 'Upload Files', 
          isActive: activeView === 'upload',
          isComplete: false,
          onClick: () => setActiveView('upload')
        },
        { 
          id: 'files', 
          label: `Uploaded Files (${uploadedFilesCount})`, 
          isActive: activeView === 'files',
          isComplete: uploadedFilesCount > 0,
          onClick: () => setActiveView('files')
        },
        { 
          id: 'review', 
          label: 'Review Data', 
          isActive: false,
          isComplete: false,
          isLocked: true,
          onClick: () => {}
        },
      ];
    }
    
    return [
      { 
        id: 'upload', 
        label: 'Upload More', 
        isActive: activeView === 'upload',
        isComplete: true,
        onClick: () => setActiveView('upload')
      },
      { 
        id: 'files', 
        label: `Uploaded Files (${uploadedFilesCount})`, 
        isActive: activeView === 'files',
        isComplete: uploadedFilesCount > 0,
        onClick: () => setActiveView('files')
      },
      { 
        id: 'summary', 
        label: 'Summary', 
        isActive: activeView === 'summary',
        isComplete: hasFinancialData,
        onClick: () => setActiveView('summary')
      },
      { 
        id: 'monthly', 
        label: `Monthly P&L (${totalPeriods})`, 
        isActive: activeView === 'monthly',
        isComplete: totalPeriods > 0,
        onClick: () => setActiveView('monthly')
      },
      { 
        id: 'transactions', 
        label: `Transactions (${totalTransactions.toLocaleString()})`, 
        isActive: activeView === 'transactions',
        isComplete: totalTransactions > 0,
        onClick: () => setActiveView('transactions')
      },
      { 
        id: 'categories', 
        label: `Categories (${categoriesCount})`, 
        isActive: activeView === 'categories',
        isComplete: categoriesCount > 0,
        onClick: () => setActiveView('categories')
      },
      { 
        id: 'tax', 
        label: hasTaxFiles ? `Tax Docs (${tax1065Files.length})` : 'Tax Docs', 
        isActive: activeView === 'tax',
        isComplete: hasTaxFiles,
        onClick: () => setActiveView('tax')
      },
      { 
        id: 'reconciliation', 
        label: 'Reconciliation', 
        isActive: activeView === 'reconciliation',
        isComplete: hasTaxFiles && hasFinancialData,
        onClick: () => setActiveView('reconciliation')
      },
      { 
        id: 'anomalies', 
        label: `Anomalies (${anomalyCount})`, 
        isActive: activeView === 'anomalies',
        isComplete: anomalyCount === 0,
        onClick: () => setActiveView('anomalies')
      },
    ];
  };

  // Build QA items for sidebar
  const buildQAItems = () => {
    const items: Array<{id: string; label: string; status: 'missing' | 'warning' | 'ok'}> = [];
    
    if (!hasFinancialData) {
      items.push({ id: 'data', label: 'Financial Data', status: 'missing' });
    }
    
    if (itemsNeedingReview > 0) {
      items.push({ id: 'review', label: `${itemsNeedingReview} items to review`, status: 'warning' });
    }
    
    if (anomalyCount > 0) {
      items.push({ id: 'anomalies', label: `${anomalyCount} anomalies`, status: 'warning' });
    }
    
    return items;
  };

  // Render Files View - Show all uploaded S3 files with download/reprocess options
  const renderFilesView = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    
    // Group files by status
    const parsedFiles = s3Files.filter(f => f.status === 'parsed');
    const failedFiles = s3Files.filter(f => f.status === 'failed');
    const processingFiles = s3Files.filter(f => f.status === 'processing' || f.status === 'uploaded');
    
    return (
      <div className="p-6 h-full overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Uploaded Files</h2>
          <p className="text-gray-600 text-sm mt-1">
            Files uploaded to S3 for this case. Click to download or retry failed extractions.
          </p>
        </div>
        
        {filesLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Loading files...</span>
          </div>
        ) : s3Files.length === 0 ? (
          <div className="text-center py-12">
            <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No files uploaded yet</p>
            <button
              onClick={() => setActiveView('upload')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Upload Files
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            <div className="flex gap-4 text-sm">
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full">
                {parsedFiles.length} Processed
              </span>
              {failedFiles.length > 0 && (
                <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full">
                  {failedFiles.length} Failed
                </span>
              )}
              {processingFiles.length > 0 && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                  {processingFiles.length} Processing
                </span>
              )}
            </div>
            
            {/* Failed Files Section */}
            {failedFiles.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Failed Extractions ({failedFiles.length})
                </h3>
                <div className="space-y-2">
                  {failedFiles.map((file) => (
                    <div key={file.upload_id || file._id} className="flex items-center justify-between bg-white p-3 rounded border">
                      <div className="flex items-center gap-3">
                        <FileWarning className="w-5 h-5 text-red-500" />
                        <div>
                          <p className="font-medium text-sm">{file.original_filename || file.filename}</p>
                          <p className="text-xs text-red-600">{file.error || 'Processing failed'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleReprocessFile(file.upload_id || file._id)}
                        disabled={reprocessingFileId === (file.upload_id || file._id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        {reprocessingFileId === (file.upload_id || file._id) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Retry
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Processed Files */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Successfully Processed ({parsedFiles.length})
              </h3>
              <div className="grid gap-2">
                {parsedFiles.map((file) => (
                  <div key={file.upload_id || file._id} className="flex items-center justify-between bg-gray-50 p-3 rounded border">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-sm">{file.original_filename || file.filename}</p>
                        <p className="text-xs text-gray-500">
                          {file.doc_type === 'tax1065' ? (
                            <>Tax Form {file.year && `(${file.year})`}</>
                          ) : (
                            <>{file.transactions_count || file.parsed_data?.transactions?.length || 0} transactions</>
                          )}
                          {file.extraction_method && ` • ${file.extraction_method}`}
                          {file.source === 'draft_data' && ' • From wizard upload'}
                        </p>
                      </div>
                    </div>
                    {file.s3_key ? (
                      <button
                        onClick={async () => {
                          try {
                            const response = await api.get(`/api/v1/reconciliation/source-pdf-url?s3_key=${encodeURIComponent(file.s3_key)}`);
                            if (response.url) {
                              window.open(response.url, '_blank');
                            }
                          } catch (err) {
                            console.error('Failed to get download URL:', err);
                          }
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        Download
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 px-2 py-1 bg-gray-100 rounded">
                        Processed
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Processing Files */}
            {processingFiles.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Loader2 className="w-5 h-5 text-yellow-500 animate-spin" />
                  Processing ({processingFiles.length})
                </h3>
                <div className="grid gap-2">
                  {processingFiles.map((file) => (
                    <div key={file.upload_id || file._id} className="flex items-center gap-3 bg-yellow-50 p-3 rounded border border-yellow-200">
                      <FileText className="w-5 h-5 text-yellow-600" />
                      <p className="text-sm">{file.original_filename || file.filename}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Refresh Button */}
            <div className="flex items-center gap-4 pt-4 border-t">
              <button
                onClick={fetchUploadedFiles}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh List
              </button>
              
              {failedFiles.length > 0 && (
                <button
                  onClick={async () => {
                    setReprocessingFileId('all-failed');
                    try {
                      const response = await api.post('/api/v1/upload-savefirst/reprocess/failed', {
                        limit: 100
                      });
                      console.log('Reprocess result:', response);
                      await fetchUploadedFiles();
                    } catch (err) {
                      console.error('Reprocess failed:', err);
                    } finally {
                      setReprocessingFileId(null);
                    }
                  }}
                  disabled={reprocessingFileId === 'all-failed'}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {reprocessingFileId === 'all-failed' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Reprocess All Failed ({failedFiles.length})
                </button>
              )}
              
              <button
                onClick={async () => {
                  setReprocessingFileId('all');
                  try {
                    // Reprocess all files with status 'processing' or 'uploaded' 
                    const response = await api.post('/api/v1/upload-savefirst/reprocess', {
                      status_filter: 'processing',
                      limit: 100
                    });
                    console.log('Reprocess result:', response);
                    await fetchUploadedFiles();
                  } catch (err) {
                    console.error('Reprocess failed:', err);
                  } finally {
                    setReprocessingFileId(null);
                  }
                }}
                disabled={reprocessingFileId === 'all'}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {reprocessingFileId === 'all' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Reprocess Stuck Files
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Upload Mode content - Full width without nested boxes
  const renderUploadMode = () => (
    <div className="h-full flex flex-col">
      {/* Main upload area - fills available space */}
      <div className="flex-1 overflow-y-auto">
        <UnifiedUploader
          onComplete={handleUploadComplete}
          onBack={() => setEntryMethod('select')}
          wizardData={data}
          compact={true}
          reportId={claimId || data.draft_id || data.id || data.report_id}
        />
      </div>
    </div>
  );

  // Render Method Selection screen (Quick Start)
  const renderMethodSelection = () => (
    <div className="p-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">How would you like to enter financial data?</h2>
          <p className="text-gray-600">Choose the method that best fits your situation</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Upload Documents Option */}
          <button
            onClick={() => {
              setEntryMethod('upload');
              setIsUploadMode(true);
              setActiveView('upload');
            }}
            className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-500 text-left group"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition">
                <Upload className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-900">Upload Documents</h3>
                <p className="text-sm text-blue-600 font-medium">Most accurate</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Upload bank statements, P&L reports, tax returns, or QuickBooks exports. Our AI extracts and categorizes your financial data automatically.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">PDF</span>
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">Excel</span>
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">CSV</span>
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">Tax Forms</span>
            </div>
          </button>

          {/* Industry Templates Option */}
          <button
            onClick={() => {
              setEntryMethod('template');
              setActiveView('template');
            }}
            className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all border-2 border-transparent hover:border-indigo-500 text-left group"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center group-hover:bg-indigo-200 transition">
                <LayoutTemplate className="w-7 h-7 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-900">Industry Templates</h3>
                <p className="text-sm text-indigo-600 font-medium">Quick start • No documents</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Don't have documents ready? Select your industry type and enter revenue estimates. We'll generate P&L data using verified industry benchmarks.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded text-xs">Restaurant</span>
              <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded text-xs">Retail</span>
              <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded text-xs">Service</span>
              <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded text-xs">+10 more</span>
            </div>
          </button>
        </div>

        {/* Back button */}
        <div className="mt-8 text-center">
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-800 font-semibold"
          >
            ← Back to Step 2
          </button>
        </div>
      </div>
    </div>
  );

  // Render Template Selection view
  const renderTemplateView = () => (
    <div className="p-6">
      <TemplateSelector
        businessType={data.business_type}
        currentRevenue={data.annual_revenue || 0}
        coverageYear={eventStartDate ? new Date(eventStartDate).getFullYear() : new Date().getFullYear()}
        onApplyTemplate={(templateData) => {
          console.log('✅ Template applied:', templateData);
          
          // Transform template financial_data format to wizard format
          // Template returns: { data_YYYY: { revenue: [...], cogs: [...], payroll: [...], expenses: [...] } }
          // Wizard expects: { data_YYYY: [{ revenue: X, cogs: Y, payroll: Z, expenses: W }, ...] }
          const transformedFinancialData: Record<string, any[]> = {};
          
          if (templateData.financial_data) {
            Object.entries(templateData.financial_data).forEach(([key, yearData]: [string, any]) => {
              if (key.startsWith('data_') && yearData) {
                // Check if data needs transformation (array format vs object format)
                if (yearData.revenue && Array.isArray(yearData.revenue)) {
                  // Transform from parallel arrays to array of objects
                  const months = yearData.revenue.length;
                  const monthlyData = [];
                  
                  for (let i = 0; i < months; i++) {
                    monthlyData.push({
                      revenue: yearData.revenue[i] || 0,
                      cogs: yearData.cogs?.[i] || 0,
                      payroll: yearData.payroll?.[i] || 0,
                      expenses: yearData.expenses?.[i] || 0,
                      month: i + 1
                    });
                  }
                  
                  transformedFinancialData[key] = monthlyData;
                } else if (Array.isArray(yearData)) {
                  // Already in correct format
                  transformedFinancialData[key] = yearData;
                }
              }
            });
          }
          
          const updatedTemplateData = {
            ...templateData,
            financial_data: transformedFinancialData
          };
          
          setAppliedTemplate(updatedTemplateData);
          
          // Update wizard data with template data
          if (onDataUpdate) {
            onDataUpdate({
              financial_data: transformedFinancialData,
              reconstructed_pl: templateData.reconstructed_pl,
              fromTemplate: true,
              applied_template: updatedTemplateData,
              template_id: templateData.template_id,
              template_name: templateData.template_name
            });
          }
          
          // Also update local state
          setAiExtractedData(transformedFinancialData);
          
          // Switch to template_complete view
          setActiveView('template_complete');
          setIsUploadMode(false);
          setEntryMethod('upload'); // Set to upload so hasExistingData logic works
        }}
      />
      
      {/* Back button */}
      <div className="mt-6 text-center">
        <button
          onClick={() => setEntryMethod('select')}
          className="text-gray-600 hover:text-gray-800 font-semibold"
        >
          ← Change Method
        </button>
      </div>
    </div>
  );

  // Render Template Complete view
  const renderTemplateComplete = () => {
    const template = appliedTemplate || data.applied_template;
    
    return (
      <div className="p-6 space-y-6">
        {/* Success Header */}
        <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border border-green-200">
          <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
            <span className="text-3xl">{template?.template_icon || '📊'}</span>
          </div>
          <div className="flex-1">
            <p className="text-green-700 font-medium flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              {template?.template_name || 'Industry Template'} applied successfully
            </p>
            <p className="text-sm text-green-600 mt-1">
              P&L estimates generated based on industry benchmarks
            </p>
          </div>
          <button
            onClick={() => {
              setEntryMethod('template');
              setActiveView('template');
            }}
            className="px-4 py-2 bg-white border border-green-300 text-green-700 rounded-lg hover:bg-green-50"
          >
            Change Template
          </button>
        </div>

        {/* Generated Data Summary */}
        {template?.financial_data && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Generated Financial Data</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(template.financial_data)
                .filter(([key]) => key.startsWith('data_'))
                .map(([key, yearData]: [string, any]) => {
                  const year = key.replace('data_', '');
                  let totalRevenue = 0;
                  
                  if (Array.isArray(yearData)) {
                    // Transformed format: [{revenue: X, ...}, ...]
                    totalRevenue = yearData.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
                  } else if (yearData?.revenue && Array.isArray(yearData.revenue)) {
                    // Original template format: {revenue: [...], ...}
                    totalRevenue = yearData.revenue.reduce((sum: number, val: number) => sum + val, 0);
                  }
                  
                  return (
                    <div key={key} className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-lg font-bold text-gray-900">{year}</div>
                      <div className="text-sm text-gray-600">
                        ${totalRevenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Industry Benchmarks */}
        {template?.benchmarks && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
            <h3 className="font-semibold text-indigo-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Industry Benchmarks Applied
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-indigo-600 font-bold">
                  {template.benchmarks.gross_margin_avg || template.defaults_used?.cogs_pct ? (100 - (template.defaults_used?.cogs_pct || 0)) : 0}%
                </div>
                <div className="text-gray-500">Gross Margin</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-indigo-600 font-bold">
                  {template.benchmarks.net_margin_avg || 0}%
                </div>
                <div className="text-gray-500">Net Margin</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-indigo-600 font-bold">
                  {template.defaults_used?.payroll_pct || 0}%
                </div>
                <div className="text-gray-500">Labor Cost</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-indigo-600 font-bold">
                  ${(template.defaults_used?.rent_monthly || 0).toLocaleString()}
                </div>
                <div className="text-gray-500">Monthly Rent</div>
              </div>
            </div>
          </div>
        )}

        {/* Event Coverage Period */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Event Coverage Period
            <span className="text-red-500 text-xs font-normal">(Required)</span>
          </h3>
          <EventCoveragePeriod
            financialData={template?.financial_data || data.financial_data}
            plFiles={[]}
            eventStartDate={eventStartDate}
            eventEndDate={eventEndDate}
            femaDeclarationNumber={femaDeclarationNumber}
            onDateChange={handleEventDateChange}
            businessAddress={data.business_address}
          />
          
          {/* Event Date Validation Warning */}
          {eventDateValidation && !eventDateValidation.is_valid && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-amber-900 text-sm">Event Date May Need Adjustment</h4>
                  <ul className="mt-1 text-sm text-amber-700 space-y-1">
                    {eventDateValidation.warnings.map((warning, idx) => (
                      <li key={idx}>• {warning}</li>
                    ))}
                  </ul>
                  {eventDateValidation.suggestions.length > 0 && (
                    <div className="mt-2 text-sm text-amber-600">
                      <strong>Suggestion:</strong> {eventDateValidation.suggestions[0]}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Industry Tips */}
        {template?.tips && template.tips.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Industry Tips for {template.template_name}
            </h3>
            <ul className="space-y-2 text-sm text-amber-800">
              {template.tips.map((tip: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="mt-1 text-amber-500">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Render Summary View content
  const renderSummaryView = () => (
    <div className="p-6 lg:p-8">
      <div className="space-y-6">
        {/* Document Requirements Checklist - Hard Gate for Accountant Queue */}
        {claimId && (
          <DocumentChecklist 
            caseId={claimId}
            onReadinessChange={(ready) => {
              console.log('📋 Document readiness changed:', ready);
            }}
          />
        )}
        
        {/* Upload Summary Stats */}
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-6 border border-emerald-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Upload Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white rounded-lg p-4 text-center shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{totalTransactions.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Transactions</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center shadow-sm">
              <div className="text-2xl font-bold text-emerald-600">{totalPeriods}</div>
              <div className="text-sm text-gray-600">Months of Data</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{categoriesCount}</div>
              <div className="text-sm text-gray-600">Categories</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center shadow-sm">
              <div className="text-2xl font-bold text-amber-600">{itemsNeedingReview}</div>
              <div className="text-sm text-gray-600">Needs Review</div>
            </div>
          </div>
          
          {dateRange && (
            <p className="text-sm text-gray-600">
              <Calendar className="w-4 h-4 inline mr-1" />
              Data covers: <strong>{dateRange}</strong>
            </p>
          )}
          
          {/* Action buttons */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => {
                setIsUploadMode(true);
                setActiveView('upload');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Upload More Files
            </button>
            <button
              onClick={() => {
                // Reset and re-upload
                setUnifiedUploadData(null);
                setAiExtractedData(null);
                setIsUploadMode(true);
                setActiveView('upload');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Re-process
            </button>
            <button
              onClick={handleRecalculateAggregates}
              disabled={isRecalculating}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-300 rounded-lg text-emerald-700 hover:bg-emerald-100 transition-colors text-sm font-medium disabled:opacity-50"
              title="Recalculate P&L totals from transaction categories"
            >
              {isRecalculating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <BarChart3 className="w-4 h-4" />
              )}
              {isRecalculating ? 'Recalculating...' : 'Recalculate P&L'}
            </button>
            <button
              onClick={handleDiagnose}
              disabled={isDiagnosing}
              className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-300 rounded-lg text-amber-700 hover:bg-amber-100 transition-colors text-sm font-medium disabled:opacity-50"
              title="Diagnose data issues and find missing values"
            >
              {isDiagnosing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
              {isDiagnosing ? 'Diagnosing...' : 'Diagnose Issues'}
            </button>
          </div>
        </div>

        {/* Diagnostics Modal */}
        {showDiagnostics && diagnosticResults && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Data Diagnostics Report
                </h3>
                <button 
                  onClick={() => setShowDiagnostics(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{diagnosticResults.total_periods}</div>
                    <div className="text-sm text-gray-600">Periods</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{diagnosticResults.total_transactions}</div>
                    <div className="text-sm text-gray-600">Transactions</div>
                  </div>
                  <div className={`rounded-lg p-4 text-center ${diagnosticResults.issues?.length > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                    <div className={`text-2xl font-bold ${diagnosticResults.issues?.length > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {diagnosticResults.issues?.length || 0}
                    </div>
                    <div className="text-sm text-gray-600">Issues Found</div>
                  </div>
                </div>
                
                {/* Issues List */}
                {diagnosticResults.issues?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3">Issues Detected</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {diagnosticResults.issues.slice(0, 10).map((issue: any, idx: number) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            issue.severity === 'high' 
                              ? 'bg-red-50 border-red-200' 
                              : 'bg-amber-50 border-amber-200'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                              issue.severity === 'high' 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {issue.severity?.toUpperCase()}
                            </span>
                            <span className="text-sm font-medium text-gray-700">{issue.period}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{issue.message}</p>
                          {issue.potential_revenue && (
                            <p className="text-xs text-emerald-600 mt-1">
                              Potential revenue: ${issue.potential_revenue.toLocaleString()}
                            </p>
                          )}
                          {issue.potential_cogs && (
                            <p className="text-xs text-orange-600 mt-1">
                              Potential COGS: ${issue.potential_cogs.toLocaleString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                    {diagnosticResults.issues.length > 10 && (
                      <p className="text-sm text-gray-500 mt-2">
                        And {diagnosticResults.issues.length - 10} more issues...
                      </p>
                    )}
                  </div>
                )}
                
                {/* Recommendations */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Recommendations</h4>
                  <div className="space-y-2">
                    {diagnosticResults.recommendations?.map((rec: string, idx: number) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <span>{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Summary Stats */}
                {diagnosticResults.summary && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Current Totals</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Revenue: <span className="font-medium text-emerald-600">${diagnosticResults.summary.total_revenue?.toLocaleString() || 0}</span></div>
                      <div>COGS: <span className="font-medium text-orange-600">${diagnosticResults.summary.total_cogs?.toLocaleString() || 0}</span></div>
                      <div>Uncategorized: <span className="font-medium">{diagnosticResults.summary.uncategorized_percentage || 0}%</span></div>
                      <div>Periods with issues: <span className="font-medium">{diagnosticResults.summary.periods_with_issues || 0}</span></div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button 
                  onClick={() => setShowDiagnostics(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Close
                </button>
                {diagnosticResults.issues?.length > 0 && (
                  <button 
                    onClick={handleFixIssues}
                    disabled={isRecalculating}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isRecalculating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Fixing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Fix All Issues
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Financial Data Table */}
        {aiExtractedData && Object.keys(aiExtractedData).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Financial Data Summary</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Period</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Revenue</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Payroll</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Fixed</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Variable</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">COGS</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Txns</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(aiExtractedData)
                    .filter(([key]) => key.startsWith('data_'))
                    .sort(([a], [b]) => a.localeCompare(b))
                    .flatMap(([yearKey, months]: [string, any]) => {
                      // Extract year from key like "data_2021" -> 2021
                      const year = parseInt(yearKey.replace('data_', ''), 10);
                      return (Array.isArray(months) ? months : []).map((m: any) => ({
                        ...m,
                        year: m.year || year,  // Use year from data or from key
                        periodKey: `${m.year || year}-${String(m.month).padStart(2, '0')}`
                      }));
                    })
                    .sort((a, b) => a.periodKey.localeCompare(b.periodKey))
                    .map((m: any) => (
                      <tr key={m.periodKey} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-900">{m.periodKey}</td>
                        <td className="text-right px-4 text-emerald-600 font-medium">
                          ${(m.revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 text-blue-600">
                          ${(m.payroll || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 text-orange-600">
                          ${(m.fixed_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 text-blue-600">
                          ${(m.variable_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 text-red-600">
                          ${(m.cogs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 text-gray-500">{m.transaction_count || 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Anomaly Banner */}
        {unifiedUploadData?.canonical_data && (
          <AnomalyBanner 
            canonicalData={unifiedUploadData.canonical_data}
            resolvedAnomalies={resolvedAnomalies}
            businessContext={{
              business_type: data.business_type,
              industry: data.naics_code,
              event_type: data.event_type,
              event_start: eventStartDate,
              event_end: eventEndDate
            }}
            enableAIAnalysis={true}
            cachedAnalysis={cachedAnomalyAnalysis}
            onAnalysisComplete={handleAnalysisComplete}
            onViewTransaction={(txnId) => {
              setHighlightedTransactionId(txnId);
              setActiveView('transactions');
            }}
            onResolveAnomaly={handleResolveAnomaly}
            onUnresolveAnomaly={handleUnresolveAnomaly}
          />
        )}

        {/* Event Coverage Period */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Event Coverage Period
          </h3>
          <EventCoveragePeriod
            financialData={aiExtractedData || data.financial_data}
            plFiles={[]}
            eventStartDate={eventStartDate}
            eventEndDate={eventEndDate}
            femaDeclarationNumber={femaDeclarationNumber}
            onDateChange={handleEventDateChange}
            businessAddress={data.business_address}
          />
        </div>
      </div>
    </div>
  );

  // Render Monthly P&L View
  const renderMonthlyView = () => (
    <div className="p-6 lg:p-8">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Monthly P&L Data</h3>
          <p className="text-sm text-gray-500 mt-1">Detailed monthly breakdown of financial data</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Period</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Revenue</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">COGS</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Gross Profit</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Payroll</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Fixed</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Variable</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Net Profit</th>
              </tr>
            </thead>
            <tbody>
              {aiExtractedData && Object.entries(aiExtractedData)
                .filter(([key]) => key.startsWith('data_'))
                .sort(([a], [b]) => a.localeCompare(b))
                .flatMap(([yearKey, months]: [string, any]) => {
                  // Extract year from key like "data_2021" -> 2021
                  const year = parseInt(yearKey.replace('data_', ''), 10);
                  return (Array.isArray(months) ? months : []).map((m: any) => ({
                    ...m,
                    year: m.year || year,
                    periodKey: `${m.year || year}-${String(m.month).padStart(2, '0')}`,
                    grossProfit: (m.revenue || 0) - (m.cogs || 0)
                  }));
                })
                .sort((a, b) => a.periodKey.localeCompare(b.periodKey))
                .map((m: any) => (
                  <tr key={m.periodKey} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium text-gray-900">{m.periodKey}</td>
                    <td className="text-right px-4 text-emerald-600 font-medium">
                      ${(m.revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 text-red-600">
                      ${(m.cogs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 text-blue-600 font-medium">
                      ${m.grossProfit.toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 text-orange-600">
                      ${(m.payroll || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 text-blue-600">
                      ${(m.fixed_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 text-gray-600">
                      ${(m.variable_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className={`text-right px-4 font-medium ${(m.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${(m.net_profit || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Transactions View
  const renderTransactionsView = () => (
    <div className="p-6 lg:p-8">
      {/* Client Clarification Panel */}
      {flaggedTransactions.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setShowClarificationPanel(!showClarificationPanel)}
            className="w-full p-4 flex items-center justify-between hover:bg-amber-100 transition"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-amber-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-amber-900">
                  {flaggedTransactions.length} Transaction{flaggedTransactions.length > 1 ? 's' : ''} Flagged for Client
                </h3>
                <p className="text-sm text-amber-700">
                  {clarificationSent ? 'Clarification request sent' : 'Ready to send clarification request'}
                </p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-amber-600 transition-transform ${showClarificationPanel ? 'rotate-180' : ''}`} />
          </button>
          
          {showClarificationPanel && (
            <div className="p-4 border-t border-amber-200 bg-white">
              {/* Flagged transactions list */}
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {flaggedTransactions.map((txn, idx) => (
                  <div key={txn.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{txn.description}</p>
                      <p className="text-xs text-gray-500">{txn.date} • ${Math.abs(txn.amount).toLocaleString()}</p>
                      <p className="text-xs text-amber-700 mt-1 italic">"{txn.question}"</p>
                    </div>
                    <button
                      onClick={() => {
                        setFlaggedTransactions(prev => prev.filter(f => f.id !== txn.id));
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Email input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Email</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
              
              {/* Send button */}
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    console.log('Send button clicked');
                    console.log('Client email:', clientEmail);
                    console.log('Flagged transactions:', flaggedTransactions);
                    
                    if (!clientEmail || flaggedTransactions.length === 0) {
                      console.log('Validation failed - missing email or no flagged transactions');
                      setValidationError('Please enter a client email and flag at least one transaction');
                      return;
                    }
                    
                    setSendingClarification(true);
                    try {
                      const apiUrl = process.env.REACT_APP_BACKEND_URL;
                      console.log('API URL:', apiUrl);
                      
                      // Get the correct report/draft ID - check multiple sources
                      const reportId = data.draft_id || data.id || data.report_id || localStorage.getItem('current_draft_id');
                      console.log('Report ID:', reportId);
                      console.log('Data keys:', Object.keys(data));
                      
                      if (!reportId) {
                        setValidationError('No draft ID found. Please save your work first.');
                        return;
                      }
                      
                      // Create clarification request
                      const createPayload = {
                        report_id: reportId,
                        claimant_email: clientEmail,
                        transactions: flaggedTransactions.map(t => ({
                          transaction_id: t.id,
                          date: t.date,
                          description: t.description,
                          amount: t.amount,
                          current_category: t.category,
                          question: t.question,
                          options: ['Cost of Goods Sold', 'Office Supplies', 'Professional Services', 'Equipment', 'Other']
                        }))
                      };
                      console.log('Create payload:', createPayload);
                      
                      // Use centralized API utility
                      const createData = await api.post('/api/v1/clarifications/create', createPayload, { showErrorToast: false });
                      console.log('Create response data:', createData);
                      const { request_id } = createData;
                      
                      // Send the email
                      console.log('Sending email for request_id:', request_id);
                      const sendData = await api.post(`/api/v1/clarifications/send/${request_id}`, {}, { showErrorToast: false });
                      console.log('Send response data:', sendData);
                      
                      setClarificationSent(true);
                      setStatusMessage('Clarification request sent successfully!');
                      
                      // Save to data
                      onDataUpdate?.({
                        ...data,
                        flagged_transactions: flaggedTransactions,
                        last_clarification_sent: new Date().toISOString(),
                        clarification_request_id: request_id
                      });
                    } catch (err: any) {
                      console.error('Failed to send clarification:', err);
                      setValidationError(`Error: ${err.message || 'Unknown error occurred'}`);
                    } finally {
                      setSendingClarification(false);
                    }
                  }}
                  disabled={sendingClarification || !clientEmail || clarificationSent}
                  className="flex-1 py-2.5 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
                >
                  {sendingClarification ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : clarificationSent ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Sent Successfully
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Clarification Request
                    </>
                  )}
                </button>
                {itemsNeedingReview > flaggedTransactions.length && (
                  <p className="text-xs text-amber-700">
                    ⚠️ {itemsNeedingReview - flaggedTransactions.length} transactions still unreviewed
                  </p>
                )}
              </div>
              
              {/* Preview note */}
              <p className="text-xs text-gray-500 mt-3">
                This will send: {flaggedTransactions.length} transaction question{flaggedTransactions.length > 1 ? 's' : ''} to {clientEmail || 'client'}
              </p>
            </div>
          )}
        </div>
      )}
      
      {unifiedUploadData?.canonical_data?.periods ? (
        <TransactionViewer 
          periods={unifiedUploadData.canonical_data.periods}
          reportId={claimId || data?.draft_id || data?.id}
          highlightedTransactionId={highlightedTransactionId}
          onHighlightClear={() => setHighlightedTransactionId(null)}
          flaggedTransactions={flaggedTransactions}
          onFlagForClient={(txn) => {
            setFlaggedTransactions(prev => {
              // Don't add if already flagged
              if (prev.some(f => f.id === txn.id)) return prev;
              return [...prev, txn];
            });
            // Auto-expand panel when first item is flagged
            if (flaggedTransactions.length === 0) {
              setShowClarificationPanel(true);
            }
          }}
          onUnflagTransaction={(txnId) => {
            setFlaggedTransactions(prev => prev.filter(f => f.id !== txnId));
          }}
          onTransactionEdit={(periodKey, txnIndex, updates) => {
            setUnifiedUploadData((prev: any) => {
              if (!prev?.canonical_data?.periods) return prev;
              
              const newPeriods = prev.canonical_data.periods.map((period: any) => {
                const pKey = `${period.year}-${String(period.month).padStart(2, '0')}`;
                if (pKey !== periodKey) return period;
                
                const newTransactions = [...(period.transactions || [])];
                if (newTransactions[txnIndex]) {
                  newTransactions[txnIndex] = {
                    ...newTransactions[txnIndex],
                    ...updates,
                    needs_review: updates.needs_review ?? newTransactions[txnIndex].needs_review,
                  };
                }
                
                return { ...period, transactions: newTransactions };
              });
              
              return {
                ...prev,
                canonical_data: {
                  ...prev.canonical_data,
                  periods: newPeriods,
                },
              };
            });
          }}
          onBulkApprove={(transactionIds) => {
            setUnifiedUploadData((prev: any) => {
              if (!prev?.canonical_data?.periods) return prev;
              
              const idSet = new Set(transactionIds);
              
              const newPeriods = prev.canonical_data.periods.map((period: any) => {
                const newTransactions = (period.transactions || []).map((txn: any, idx: number) => {
                  const txnId = `${period.year}-${String(period.month).padStart(2, '0')}-${idx}`;
                  if (idSet.has(txnId)) {
                    return {
                      ...txn,
                      validation_status: 'approved',
                      needs_review: false,
                    };
                  }
                  return txn;
                });
                
                return { ...period, transactions: newTransactions };
              });
              
              return {
                ...prev,
                canonical_data: {
                  ...prev.canonical_data,
                  periods: newPeriods,
                },
              };
            });
          }}
        />
      ) : (
        <div className="text-center py-12 text-gray-500">
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No transaction data available.</p>
          <p className="text-sm mt-2">Upload financial documents to see transactions.</p>
        </div>
      )}
    </div>
  );

  // Render Categories View
  const renderCategoriesView = () => {
    // Group transactions by category
    const categoryTotals: Record<string, { count: number; amount: number }> = {};
    periods.forEach((period: any) => {
      (period.transactions || []).forEach((txn: any) => {
        const cat = txn.category || 'Uncategorized';
        if (!categoryTotals[cat]) {
          categoryTotals[cat] = { count: 0, amount: 0 };
        }
        categoryTotals[cat].count++;
        categoryTotals[cat].amount += Math.abs(txn.amount || 0);
      });
    });

    const sortedCategories = Object.entries(categoryTotals)
      .sort((a, b) => b[1].amount - a[1].amount);

    return (
      <div className="p-6 lg:p-8">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Expense Categories</h3>
            <p className="text-sm text-gray-500 mt-1">{categoriesCount} categories identified</p>
          </div>
          <div className="divide-y divide-gray-100">
            {sortedCategories.map(([category, data]) => (
              <div key={category} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Folder className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{category}</p>
                    <p className="text-sm text-gray-500">{data.count} transactions</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">
                    ${data.amount.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render Tax Documents View
  const renderTaxView = () => {
    const taxFiles = tax1065Files.length > 0 ? tax1065Files : (data.tax1065Files || []);
    const form1065s = taxFiles.filter((f: any) => f.form_type === '1065' || f.data?.form_type === '1065');
    const k1Forms = taxFiles.filter((f: any) => {
      const formType = f.form_type || f.data?.form_type;
      return formType === 'K-1' || formType === 'K1' || formType?.toLowerCase() === 'k-1';
    });

    return (
      <div className="p-6 lg:p-8">
        <div className="space-y-6">
          {taxFiles.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600 font-medium">No tax documents uploaded yet</p>
              <p className="text-sm text-gray-500 mt-2">Upload 1065 or K-1 forms to see tax data</p>
              <button
                onClick={() => {
                  setIsUploadMode(true);
                  setActiveView('upload');
                }}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Upload Tax Forms
              </button>
            </div>
          ) : (
            <>
              {/* Form 1065 Records */}
              {form1065s.length > 0 && (
                <div className="bg-blue-50 rounded-xl border border-blue-200 overflow-hidden">
                  <div className="bg-blue-100 px-6 py-4 border-b border-blue-200">
                    <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Form 1065 - Partnership Return ({form1065s.length})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-blue-100/50">
                        <tr>
                          <th className="text-left p-4 font-semibold text-blue-900">Year</th>
                          <th className="text-right p-4 font-semibold text-blue-900">Gross Receipts</th>
                          <th className="text-right p-4 font-semibold text-blue-900">COGS</th>
                          <th className="text-right p-4 font-semibold text-blue-900">Salaries/Wages</th>
                          <th className="text-right p-4 font-semibold text-blue-900">Guaranteed Pay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form1065s.map((file: any, idx: number) => {
                          const taxData = file.data || {};
                          return (
                            <tr key={idx} className="border-t border-blue-200">
                              <td className="p-4 font-mono font-bold text-blue-800">
                                {taxData.tax_year || file.year || '-'}
                              </td>
                              <td className="p-4 text-right font-mono text-gray-700">
                                ${(taxData.gross_receipts || 0).toLocaleString()}
                              </td>
                              <td className="p-4 text-right font-mono text-gray-700">
                                ${(taxData.cost_of_goods_sold || 0).toLocaleString()}
                              </td>
                              <td className="p-4 text-right font-mono font-bold text-blue-700">
                                ${(taxData.salaries_wages || 0).toLocaleString()}
                              </td>
                              <td className="p-4 text-right font-mono text-gray-700">
                                ${(taxData.guaranteed_payments || 0).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Schedule K-1 Records */}
              {k1Forms.length > 0 && (
                <div className="bg-emerald-50 rounded-xl border border-emerald-200 overflow-hidden">
                  <div className="bg-emerald-100 px-6 py-4 border-b border-emerald-200">
                    <h3 className="font-semibold text-emerald-900 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Schedule K-1 - Partner's Share ({k1Forms.length})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-emerald-100/50">
                        <tr>
                          <th className="text-left p-4 font-semibold text-emerald-900">Year</th>
                          <th className="text-left p-4 font-semibold text-emerald-900">Partner</th>
                          <th className="text-right p-4 font-semibold text-emerald-900">Box 1: Ordinary</th>
                          <th className="text-right p-4 font-semibold text-emerald-900">Box 4: Guaranteed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {k1Forms.map((file: any, idx: number) => {
                          const taxData = file.data || {};
                          const ordinaryIncome = taxData.ordinary_income || taxData.schedule_k1?.box_1_ordinary_income || 0;
                          return (
                            <tr key={idx} className="border-t border-emerald-200">
                              <td className="p-4 font-mono font-bold text-emerald-800">
                                {taxData.tax_year || file.year || '-'}
                              </td>
                              <td className="p-4 text-gray-700">
                                {taxData.partner_name || 'Unknown'}
                              </td>
                              <td className="p-4 text-right font-mono font-bold text-emerald-700">
                                ${ordinaryIncome.toLocaleString()}
                              </td>
                              <td className="p-4 text-right font-mono text-gray-700">
                                ${(taxData.guaranteed_payments || taxData.schedule_k1?.box_4_guaranteed_payments || 0).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Render Reconciliation View - P&L vs 1065 Cross-Check
  const renderReconciliationView = () => {
    // Get tax files data
    const taxFiles = tax1065Files || [];
    const form1065s = taxFiles.filter((f: any) => f.form_type === '1065');
    const k1Forms = taxFiles.filter((f: any) => f.form_type === 'K-1' || f.form_type === 'K1');
    
    return (
      <div className="p-6 h-full overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">P&L ↔ 1065 Reconciliation</h2>
          <p className="text-gray-600 text-sm mt-1">
            Cross-check bank-derived P&L data against tax-reported 1065 figures
          </p>
        </div>
        
        <TaxReconciliation 
          plData={aiExtractedData}
          tax1065Files={form1065s}
          k1Forms={k1Forms}
        />
      </div>
    );
  };

  // Render Anomalies View
  const renderAnomaliesView = () => {
    // Try multiple sources for canonical data: local state, data prop, or build from aiExtractedData
    const canonicalData = unifiedUploadData?.canonical_data 
      || data.unified_upload_data?.canonical_data
      || (aiExtractedData ? buildCanonicalFromFinancialData(aiExtractedData) : null);
    
    // Transform anomalies for the enhanced resolver
    const transformedAnomalies = cachedAnomalyAnalysis?.anomalies?.map((a: any) => {
      // Calculate deviation and amounts from anomaly data
      const deviation = a.deviation_percent || 0;
      const relatedAmount = a.related_amount || 0;
      const isSpike = deviation > 0;
      
      // Extract period info
      const period = a.affected_period || '';
      const [year, month] = period.split('-').map(Number);
      
      // Find surrounding periods for from/to values
      const periods = canonicalData?.periods || [];
      const currentPeriodIdx = periods.findIndex((p: any) => p.year === year && p.month === month);
      const currentPeriod = periods[currentPeriodIdx];
      const prevPeriod = periods[currentPeriodIdx - 1];
      
      const fromValue = prevPeriod?.gross_revenue || 0;
      const toValue = currentPeriod?.gross_revenue || relatedAmount;
      
      // Build category breakdown if transactions are available
      const categoryBreakdown: any[] = [];
      if (currentPeriod?.transactions && prevPeriod?.transactions) {
        const currCategories = new Map<string, number>();
        const prevCategories = new Map<string, number>();
        
        currentPeriod.transactions.forEach((t: any) => {
          const cat = t.category || 'Uncategorized';
          currCategories.set(cat, (currCategories.get(cat) || 0) + Math.abs(t.amount || 0));
        });
        
        prevPeriod.transactions.forEach((t: any) => {
          const cat = t.category || 'Uncategorized';
          prevCategories.set(cat, (prevCategories.get(cat) || 0) + Math.abs(t.amount || 0));
        });
        
        // Get top 5 categories by change
        const allCategories = new Set([...currCategories.keys(), ...prevCategories.keys()]);
        allCategories.forEach(cat => {
          const prev = prevCategories.get(cat) || 0;
          const curr = currCategories.get(cat) || 0;
          if (Math.abs(curr - prev) > 0) {
            categoryBreakdown.push({
              name: cat,
              prev,
              curr,
              delta: curr - prev
            });
          }
        });
        
        categoryBreakdown.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));
      }
      
      // Get top transactions for this anomaly period
      // If anomaly has transaction_ids, use those; otherwise get top transactions by amount from the period
      let topTransactions: any[] = [];
      
      if (a.transactions && a.transactions.length > 0) {
        // Use pre-computed transactions from backend (new anomalies)
        topTransactions = a.transactions;
      } else if (currentPeriod?.transactions && currentPeriod.transactions.length > 0) {
        // Augment with top transactions from period data (for old anomalies missing transaction data)
        // Sort by absolute amount to get the most significant transactions
        const sortedTxns = [...currentPeriod.transactions]
          .filter((t: any) => t.amount !== 0)
          .sort((x: any, y: any) => Math.abs(y.amount || 0) - Math.abs(x.amount || 0));
        
        topTransactions = sortedTxns.slice(0, 5).map((txn: any) => ({
          id: txn.id || txn._txn_id,
          date: txn.transaction_date || txn.date || period,
          description: (txn.description || txn.payee || 'Transaction').slice(0, 60),
          amount: txn.amount || 0,
          category: txn.category || 'Uncategorized',
          flag: Math.abs(txn.amount || 0) > 5000 // Flag large transactions
        }));
      } else if (a.transaction_ids && a.transaction_ids.length > 0) {
        // Fallback: try to find by transaction_ids
        topTransactions = a.transaction_ids
          .slice(0, 5)
          .map((txnId: string) => {
            const txn = currentPeriod?.transactions?.find((t: any) => 
              t.id === txnId || t._txn_id === txnId
            );
            return txn ? {
              id: txn.id || txn._txn_id,
              date: txn.transaction_date || txn.date || period,
              description: (txn.description || txn.payee || 'Transaction').slice(0, 60),
              amount: txn.amount || 0,
              category: txn.category || 'Uncategorized',
              flag: true
            } : null;
          })
          .filter(Boolean);
      }
      
      return {
        id: a.id,
        severity: a.severity || 'medium',
        type: a.anomaly_type?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Revenue Change',
        title: a.title || a.description,
        description: a.description || '',
        percent: Math.abs(deviation),
        from: fromValue,
        to: toValue,
        periodFrom: prevPeriod ? `${prevPeriod.year}-${String(prevPeriod.month).padStart(2, '0')}` : '',
        periodTo: period,
        period: period,
        deviation: deviation,
        categories: a.categories || categoryBreakdown.slice(0, 5),
        transactions: topTransactions,
        aiSuggestion: a.ai_analysis?.explanation || a.explanation,
        aiConfidence: a.ai_analysis?.confidence,
        sourceDoc: a.source_document,
        lossEvent: a.anomaly_type === 'revenue_drop' && Math.abs(deviation) > 50,
        is_resolved: a.is_resolved,
        resolution_note: a.resolution_note,
        resolved_at: a.resolved_at,
        resolution_reason: a.resolution_reason,
        anomaly_type: a.anomaly_type,
        explanation: a.explanation,
        ai_analysis: a.ai_analysis,
        forensic_insight: a.forensic_insight,
        forensic_analysis: a.forensic_analysis
      };
    }) || [];
    
    // Build periods data for timeline
    const periodsData = canonicalData?.periods?.map((p: any) => {
      const month = `${p.year}-${String(p.month).padStart(2, '0')}`;
      const hasAnomaly = transformedAnomalies.some((a: any) => a.period === month && !a.is_resolved);
      const anomalyType = transformedAnomalies.find((a: any) => a.period === month)?.deviation;
      return {
        month,
        revenue: p.gross_revenue || 0,
        anomaly: hasAnomaly ? (anomalyType && anomalyType < 0 ? 'drop' : 'spike') as 'spike' | 'drop' : undefined
      };
    }) || [];
    
    // Calculate totals
    const totalTransactions = canonicalData?.periods?.reduce((sum: number, p: any) => 
      sum + (p.transactions?.length || 0), 0) || 0;
    const totalMonths = canonicalData?.periods?.length || 0;
    
    // Handle batch resolve
    const handleBatchResolve = async (ids: string[], reason: string, note: string) => {
      for (const id of ids) {
        await handleResolveAnomaly(id, `${reason}: ${note}`);
      }
    };
    
    return (
      <div className="p-6 lg:p-8">
        {canonicalData?.periods?.length > 0 ? (
          transformedAnomalies.length > 0 ? (
            <AnomalyResolver
              anomalies={transformedAnomalies}
              periodsData={periodsData}
              totalTransactions={totalTransactions}
              totalMonths={totalMonths}
              onResolve={(id, reason, note) => handleResolveAnomaly(id, `${reason}: ${note}`)}
              onBatchResolve={handleBatchResolve}
              onUnresolve={handleUnresolveAnomaly}
              onViewTransaction={(txnId) => {
                setHighlightedTransactionId(txnId);
                setActiveView('transactions');
              }}
              onForensicInsightFetched={handleForensicInsightFetched}
            />
          ) : (
            <AnomalyBanner 
              canonicalData={canonicalData}
              resolvedAnomalies={resolvedAnomalies}
              businessContext={{
                business_type: data.business_type,
                industry: data.naics_code,
                event_type: data.event_type,
                event_start: eventStartDate,
                event_end: eventEndDate
              }}
              enableAIAnalysis={true}
              cachedAnalysis={cachedAnomalyAnalysis}
              onAnalysisComplete={handleAnalysisComplete}
              onViewTransaction={(txnId) => {
                setHighlightedTransactionId(txnId);
                setActiveView('transactions');
              }}
              onResolveAnomaly={handleResolveAnomaly}
              onUnresolveAnomaly={handleUnresolveAnomaly}
            />
          )
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 font-medium">No data to analyze</p>
            <p className="text-sm text-gray-500 mt-2">Upload financial documents first</p>
          </div>
        )}
      </div>
    );
  };

  // Render active view content
  // Wrapper to add DocumentChecklist to applicable views
  const withDocumentChecklist = (content: React.ReactNode, showChecklist: boolean = true) => {
    if (!showChecklist || !claimId) {
      return content;
    }
    return (
      <div className="space-y-6">
        {/* Document Requirements Checklist - Hard Gate for Accountant Queue */}
        <DocumentChecklist 
          caseId={claimId}
          compact={activeView !== 'summary'} // Compact mode except for summary
          onReadinessChange={(ready) => {
            console.log('📋 Document readiness changed:', ready);
          }}
        />
        {content}
      </div>
    );
  };

  const renderContent = () => {
    // Method selection screen - no checklist here
    if (entryMethod === 'select') {
      return renderMethodSelection();
    }
    
    // Template mode - show checklist
    if (entryMethod === 'template' || activeView === 'template') {
      return withDocumentChecklist(renderTemplateView());
    }
    
    // Template complete view - show checklist
    if (activeView === 'template_complete') {
      return withDocumentChecklist(renderTemplateComplete());
    }
    
    // Upload mode - show checklist (important - users need to see what's missing)
    if (isUploadMode || activeView === 'upload') {
      return withDocumentChecklist(renderUploadMode());
    }
    
    // Files view - show uploaded S3 files with checklist
    if (activeView === 'files') {
      return withDocumentChecklist(renderFilesView());
    }
    
    switch (activeView) {
      case 'summary':
        // Summary already has checklist integrated at top, skip wrapper
        return renderSummaryView();
      case 'monthly':
        return withDocumentChecklist(renderMonthlyView());
      case 'transactions':
        return withDocumentChecklist(renderTransactionsView());
      case 'categories':
        return withDocumentChecklist(renderCategoriesView());
      case 'tax':
        return withDocumentChecklist(renderTaxView());
      case 'reconciliation':
        return withDocumentChecklist(renderReconciliationView());
      case 'anomalies':
        return withDocumentChecklist(renderAnomaliesView());
      default:
        return renderSummaryView();
    }
  };

  // Determine subtitle based on mode
  const getSubtitle = () => {
    if (entryMethod === 'select') {
      return "Choose how you'd like to enter your financial data";
    }
    if (entryMethod === 'template' || activeView === 'template') {
      return "Select your industry and enter revenue to generate P&L estimates";
    }
    if (activeView === 'template_complete') {
      return `${appliedTemplate?.template_name || 'Industry Template'} - P&L generated from benchmarks`;
    }
    if (isUploadMode) {
      return "Upload your business financial documents for AI-powered analysis";
    }
    return `Reviewing ${totalTransactions.toLocaleString()} transactions across ${totalPeriods} months`;
  };

  // Handle next for template mode
  const handleTemplateNext = () => {
    const template = appliedTemplate || data.applied_template;
    
    if (!eventStartDate || !eventEndDate) {
      setValidationError('Please enter the Event Coverage Period dates before continuing.');
      return;
    }
    
    // Debug logging
    console.log('🔄 handleTemplateNext - current data.calculation_mode:', data.calculation_mode);
    console.log('🔄 handleTemplateNext - template:', template);
    console.log('🔄 handleTemplateNext - financial_data:', template?.financial_data || aiExtractedData);
    
    const nextData = {
      ...data,
      financial_data: template?.financial_data || aiExtractedData,
      fromTemplate: true,
      applied_template: template,
      template_id: template?.template_id,
      template_name: template?.template_name,
      event_start_date: eventStartDate,
      event_end_date: eventEndDate,
      fema_declaration_number: femaDeclarationNumber,
      // Explicitly preserve calculation_mode
      calculation_mode: data.calculation_mode,
    };
    
    console.log('🔄 handleTemplateNext - nextData to pass:', nextData);
    console.log('🔄 handleTemplateNext - calculation_mode being passed:', nextData.calculation_mode);
    
    onNext(nextData);
  };

  // Handle Quick Actions from sidebar
  const handleQuickAction = (action: string) => {
    console.log('⚡ Quick Action triggered:', action);
    switch (action) {
      case 'upload':
        // Switch to upload view
        setActiveView('upload');
        setIsUploadMode(true);
        break;
      case 'clarification':
        // Switch to transactions view where user can flag items for clarification
        if (hasFinancialData) {
          setActiveView('transactions');
          setIsUploadMode(false);
        } else {
          // If no financial data, prompt to upload first
          setActiveView('upload');
          setIsUploadMode(true);
        }
        break;
      default:
        console.log('Unknown quick action:', action);
    }
  };

  // shouldShowMethodSelection is now computed early in the component (after entryMethod)

  return (
    <WizardV2Layout
      currentStep={3}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Financial Data"
      stepSubtitle={getSubtitle()}
      completionPercent={completionPercent}
      grade={itemsNeedingReview === 0 && hasFinancialData ? 'B' : hasFinancialData ? 'C' : 'D'}
      calculationMode={data.calculation_mode || 'standard_bi'}
      sections={shouldShowMethodSelection ? [] : buildSections()}
      qaItems={shouldShowMethodSelection ? [] : buildQAItems()}
      nextAction={shouldShowMethodSelection ? undefined : (!hasFinancialData ? {
        label: 'Upload financial documents',
        description: 'Required to continue'
      } : itemsNeedingReview > 0 ? {
        label: `Review ${itemsNeedingReview} items`,
        description: 'Recommended before continuing'
      } : undefined)}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={activeView === 'template_complete' ? handleTemplateNext : handleNext}
      claimId={claimId}
      nextLabel={activeView === 'template_complete' ? "Continue to Date Selection" : "Continue to Date Selection"}
      isNextDisabled={!hasFinancialData || (activeView === 'template_complete' && (!eventStartDate || !eventEndDate))}
      showNext={!isUploadMode && !shouldShowMethodSelection && (hasFinancialData || activeView === 'template_complete')}
      onQuickAction={handleQuickAction}
    >
      {validationError && (
        <p className="text-red-500 text-sm mb-4 px-6">{validationError}</p>
      )}
      {statusMessage && (
        <p className="text-green-600 text-sm mb-4 px-6">{statusMessage}</p>
      )}
      {renderContent()}
    </WizardV2Layout>
  );
}
